import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import TailwindModal from '../ui/TailwindModal'
import './LevelUpModal.css'

/**
 * Character / kingdom level-up toast.
 *
 * Account (user) level-ups are NOT handled here — they go to `components/LevelRewardModal`,
 * which owns the whole account-level celebration including the reward claim. Keeping a second
 * user-level modal alive just meant two components racing to announce the same event.
 */
interface LevelUpModalProps {
  open: boolean
  onClose: () => void
  type: 'character' | 'kingdom'
  level: number
  characterName?: string
  characterImage?: string
  characterId?: string
}

const LevelUpModal: React.FC<LevelUpModalProps> = ({
  open,
  onClose,
  type,
  level,
  characterName,
  characterImage,
  characterId,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isVisible, setIsVisible] = useState(false)

  const title =
    type === 'kingdom' ? t('levelUpModal.kingdomLevelUp') : t('levelUpModal.characterLevelUp')
  const subtitle =
    type === 'kingdom'
      ? t('levelUpModal.kingdomReachedLevel', { level })
      : characterName
        ? t('levelUpModal.characterReachedLevel', { characterName, level })
        : t('levelUpModal.characterReachedLevelGeneric', { level })

  // Kingdom is a passive announcement; character level-ups keep their "Upgrade" CTA and wait.
  const autoCloses = type === 'kingdom'

  useEffect(() => {
    if (open) {
      setIsVisible(true)
      if (autoCloses) {
        const timer = setTimeout(() => {
          setIsVisible(false)
          setTimeout(() => {
            onClose()
          }, 300)
        }, 3000)
        return () => clearTimeout(timer)
      }
    } else {
      setIsVisible(false)
    }
  }, [open, autoCloses, onClose])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(() => {
      onClose()
    }, 300)
  }

  const handleViewCharacter = () => {
    handleClose()
    if (characterId) {
      navigate('/characters', { state: { characterId } })
    } else {
      navigate('/characters')
    }
  }

  return (
    <TailwindModal
      open={open}
      onClose={autoCloses ? undefined : handleClose}
      className="level-up-modal"
      disableEscapeKeyDown={autoCloses}
      zIndex={10000}
      backdropStyle={{
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        pointerEvents: autoCloses ? 'none' : 'auto',
      }}
    >
      <div 
        className={`level-up-modal-container ${isVisible ? 'level-up-modal-visible' : ''}`}
        style={{
          position: 'relative',
          outline: 'none',
          opacity: isVisible ? 1 : 0,
        }}
      >
        {/* Background glow effect */}
        <div className="level-up-glow" />

        {/* Sparkle effects */}
        <div className="level-up-sparkle sparkle-1" />
        <div className="level-up-sparkle sparkle-2" />
        <div className="level-up-sparkle sparkle-3" />
        <div className="level-up-sparkle sparkle-4" />

        {/* Main content */}
        <div className="level-up-content">
          {/* Character Image or Icon */}
          {type === 'character' && characterImage ? (
            <div className={`level-up-character-image ${isVisible ? 'level-up-character-image-animate' : ''}`}>
              <img
                src={characterImage}
                alt={characterName || t('levelUpModal.characterAlt')}
              />
            </div>
          ) : (
            <div className={`level-up-icon ${isVisible ? 'level-up-icon-animate' : ''}`}>
              {type === 'kingdom' ? '👑' : '⭐'}
            </div>
          )}

          {/* Title */}
          <div className={`level-up-title ${isVisible ? 'level-up-title-animate' : ''}`}>
            <h2 className="level-up-title-text" id="levelup-modal-title">{title}</h2>
          </div>

          {/* Subtitle with level inline */}
          <div className={`level-up-subtitle ${isVisible ? 'level-up-subtitle-animate' : ''}`}>
            <p>{subtitle}</p>
          </div>

          {/* Button - Only for character level ups */}
          {type === 'character' && characterId && (
            <button
              onClick={handleViewCharacter}
              className={`level-up-button ${isVisible ? 'level-up-button-animate' : ''}`}
            >
              {t('levelUpModal.upgrade')}
            </button>
          )}
        </div>
      </div>
    </TailwindModal>
  )
}

export default LevelUpModal